/**
 * @flow
 */

import React from 'react';
import styled from 'styled-components';
import { useFormContext, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Form, Input, Label } from 'semantic-ui-react';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import { map } from 'lodash';

import { FormLabel, ColorPicker } from '../../../../../components';
import ServiceDurationSelect from '../../../containers/service/service/ServiceDurationSelect';

type ServiceFormProps = {
    intl: Object,
    onSubmit: Function,
};

const intlMessages = defineMessages({
    serviceNamePlaceholder: {
        id: 'pages.settings.services.serviceForm.namePlaceholder',
        defaultMessage: 'Service name',
    },
    serviceNameError: {
        id: 'pages.settings.services.add.serviceNameError',
        defaultMessage: 'Enter service name',
    },
    serviceDurationPlaceholder: {
        id: 'pages.settings.services.serviceForm.durationPlaceholder',
        defaultMessage: 'Service duration',
    },
    serviceDurationError: {
        id: 'pages.settings.services.add.serviceDurationError',
        defaultMessage: 'Enter duration the services requries',
    },
    servicePricePlaceholder: {
        id: 'pages.settings.services.serviceForm.pricePlaceholder',
        defaultMessage: 'Price of the service',
    },
    servicePriceError: {
        id: 'pages.settings.services.add.servicePriceError',
        defaultMessage: 'Enter service price',
    },
    serviceColorError: {
        id: 'pages.settings.services.add.serviceColorError',
        defaultMessage: 'Select color that visualy identifies service',
    },
});

const ServiceNameFieldGroup = styled(Form.Group)`
    & .field {
        display: flex;
        align-items: center;
    }
`;

const ServiceNameFieldLabel = styled(FormLabel)`
    flex: 0 0 auto;
`;

const ServiceNameField = styled(Input)`
    flex: 1;
    margin-left: 5px;
`;

function ServiceForm({ intl, onSubmit }: ServiceFormProps) {
    const { control, handleSubmit, formState: { errors } } = useFormContext();
    const languages = useSelector(({ intl: intlState }) =>
        intlState.get('languages'),
    );
    const defaultLanguage = useSelector(({ intl: intlState }) =>
        intlState.get('defaultLanguage'),
    );

    return (
        <Form onSubmit={handleSubmit(onSubmit)}>
            <FormLabel>
                <FormattedMessage
                    id="pages.settings.services.serviceForm.name"
                    defaultMessage="Name"
                />
            </FormLabel>
            <ServiceNameFieldGroup widths="equal">
                {map(languages, lang => (
                    <Form.Field
                        key={lang}
                        error={
                            !!(
                                errors.nameVariants &&
                                errors.nameVariants[lang]
                            )
                        }
                        style={{ display: 'flex', alignItems: 'center' }}
                    >
                        <ServiceNameFieldLabel>
                            {lang.toUpperCase()}
                        </ServiceNameFieldLabel>
                        <Controller
                            name={`nameVariants.${lang}`}
                            control={control}
                            rules={
                                lang === defaultLanguage
                                    ? {
                                          required: intl.formatMessage(
                                              intlMessages.serviceNameError,
                                          ),
                                      }
                                    : {}
                            }
                            render={({ field: { ref: _ref, ...field } }) => (
                                <ServiceNameField
                                    {...field}
                                    placeholder={intl.formatMessage(
                                        intlMessages.serviceNamePlaceholder,
                                    )}
                                    fluid
                                />
                            )}
                        />
                        {errors.nameVariants && errors.nameVariants[lang] && (
                            <Label color="red" pointing basic>
                                {errors.nameVariants[lang].message}
                            </Label>
                        )}
                    </Form.Field>
                ))}
            </ServiceNameFieldGroup>
            <Form.Field error={!!errors.duration}>
                <FormLabel>
                    <FormattedMessage
                        id="pages.settings.services.serviceForm.duration"
                        defaultMessage="Duration"
                    />
                </FormLabel>
                <Controller
                    name="duration"
                    control={control}
                    rules={{
                        required: intl.formatMessage(intlMessages.serviceDurationError),
                    }}
                    render={({ field: { ref: _ref, value, onChange, ...field } }) => (
                        <ServiceDurationSelect
                            {...field}
                            value={value || undefined}
                            onChange={(e, data) => onChange(data.value)}
                            placeholder={intl.formatMessage(
                                intlMessages.serviceDurationPlaceholder,
                            )}
                        />
                    )}
                />
                {errors.duration && (
                    <Label color="red" pointing basic>
                        {errors.duration.message}
                    </Label>
                )}
            </Form.Field>
            <Form.Field error={!!errors.price}>
                <FormLabel>
                    <FormattedMessage
                        id="pages.settings.services.serviceForm.price"
                        defaultMessage="Price"
                    />
                </FormLabel>
                <Controller
                    name="price"
                    control={control}
                    rules={{
                        required: intl.formatMessage(intlMessages.servicePriceError),
                    }}
                    render={({ field: { ref: _ref, ...field } }) => (
                        <Input
                            {...field}
                            placeholder={intl.formatMessage(
                                intlMessages.servicePricePlaceholder,
                            )}
                            fluid
                        />
                    )}
                />
                {errors.price && (
                    <Label color="red" pointing basic>
                        {errors.price.message}
                    </Label>
                )}
            </Form.Field>
            <Form.Field error={!!errors.color}>
                <FormLabel>
                    <FormattedMessage
                        id="pages.settings.services.serviceForm.color"
                        defaultMessage="Color"
                    />
                </FormLabel>
                <Controller
                    name="color"
                    control={control}
                    rules={{
                        required: intl.formatMessage(intlMessages.serviceColorError),
                    }}
                    render={({ field: { ref: _ref, value, onChange } }) => (
                        <ColorPicker value={value} onChange={onChange} />
                    )}
                />
                {errors.color && (
                    <Label color="red" pointing basic>
                        {errors.color.message}
                    </Label>
                )}
            </Form.Field>
        </Form>
    );
}

export default injectIntl(ServiceForm);
